import {Box, CircularProgress, debounce, InputAdornment} from '@mui/material';
import React, {FunctionComponent, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {CustomTooltip} from '../../../../../styles/components/CustomTooltip';
import {errorBigIcon, iconNotifyWarningInfo, searchIcon, toastSuccess,} from '../../../../../assets/icons/icons';
import {Button, Typography} from '../../../../../components/StyledComponents';
import {
  EPaymentRequiredType,
  EPlatformsNames,
  ESubscriptionPlans,
  ETrialStatus,
  SubscriptionMockData
} from '../../../../../models/consts';
import {IError, IPaymentRequiredPoppup, PlatformProps} from '../../../../../models/inner-models';
import Platform from '../../../../../models/platforms-data';
import {
  backupShopifyItem,
  deleteGithubItem,
  fetchShopifyEntity,
  fetchShopifyUser,
  getAllSubscriptions,
} from '../../../../../store/actions';
import {useAppDispatch, useAppSelector} from '../../../../../store/hooks';
import {
  getIsTestUser,
  getShopifyAppInstallSelector,
  getShopifyCanUserBackup,
  getShopifyConfirmedSelector,
  getShopifyEntitySelector,
  getShopifyTotal,
  getShopifyUsersSelector,
  isShopifyEntityLoadingSelector,
  isShopifyUsersLoadingSelector,
  isSubUser,
  loadingUser,
  roleByApp,
  shopifySubscription,
  shopifyTrialHoursLeft,
  shopifyTrialPlanQuantity,
  shopifyTrialQuantityLeft,
  shopifyTrialStatus,
} from '../../../../../store/selectors';
import {SearchInput} from '../../../../../styles/components/MainInputElements';
import getSubInfo from '../../../../../utils/platformSubscriptionInfo';
import withRedirectAfterLogout from '../../../../../utils/withRedirectAfterLogout';
import fetchItemsErrorHandler from "../../../../../utils/fetchItemsErrorHandler";
import Swal from "sweetalert2";
import {decoratePopUpMessage} from "../../../../../utils/popUpTextDecorator";
import PlatformItemsList from "../../../../../components/PlatformComponents/PlatformItemsList/PlatformItemsList";
import PaymentRequiredPoppup from "../../../../../components/Dialogs/PaymentRequiredPoppup/PaymentRequiredPoppup";
import {EWebsocketMethods} from "../../../../../constants";
import NoSubscriptionBlock from "../../../../../components/Blocks/NoSubscriptionBlock/NoSubscriptionBlock";
import NoBackupsBlock from "../../../../../components/Blocks/NoItemsBlock/NoItemsBlock";
import UserNotConfirmedBlock from "../../../../../components/Blocks/UserNotConfirmedBlock/UserNotConfirmedBlock";
import Modal from "../../../../../components/Dialogs/Modal/Modal";
import DeleteItemDialog from "../../../../../components/Dialogs/DeleteItemDialog/DeleteItemDialog";
import DatabaseIcoInvert from "../../../../../assets/icons/CustomSvg/DatabaseIcoInvert";
import {IDeletePayload} from "../../../../../models/models";
import {
  BadgesAddingItems,
  BadgesAppWasDeleted,
  BadgesBackup,
  BadgesIFrameMode,
  BadgesSubscription,
  BadgesTrial
} from "../../../../../components/Badges/Badges";
import CustomTimerTooltip from "../../../../../styles/components/CustomTimerTooltip";
import {updateSubscriptionPlanModal} from "../../../../../utils/functions/updateSubscriptionPlanModal";
import ExternalStorage from "../../../../UserPages/UserSettings/ExternalStorage/ExternalStorage";
import {useWebSocket} from "../../../../../contexts/WebSocketContext";
import {iniFrame} from "../../../../../serviceWorker";
import {ArrowLeft} from "react-feather";

const ShopifyEntityList: FunctionComponent = () => {
  const {t: translate} = useTranslation();
  const { entityType } = useParams();
  const userLoading = useAppSelector(loadingUser);
  const isLoadingRepos = useAppSelector(isShopifyEntityLoadingSelector);
  const isLoadingGithubUsers = useAppSelector(isShopifyUsersLoadingSelector);
  const trialState = useAppSelector(shopifyTrialStatus);
  const repos = useAppSelector(getShopifyEntitySelector);
  const total = useAppSelector(getShopifyTotal);
  const canUserBackup = useAppSelector(getShopifyCanUserBackup);
  const isTestUser = useAppSelector(getIsTestUser);
  const trialDays = useAppSelector(shopifyTrialHoursLeft);
  const trialQuantity = useAppSelector(shopifyTrialQuantityLeft);
  const trialPlanQuantity = useAppSelector(shopifyTrialPlanQuantity);
  const subInfo = useAppSelector(shopifySubscription);
  const users = useAppSelector(getShopifyUsersSelector);
  const hasUserApp = useAppSelector(getShopifyAppInstallSelector);
  const isShopConfirmed = useAppSelector(getShopifyConfirmedSelector);
  const isUserSub = useAppSelector(isSubUser);
  const roleApp = useAppSelector(roleByApp);
  const isAdminRole = isUserSub && roleApp?.shopify === 'admin' && (trialState === 'active' || subInfo?.planName === 'Pro') || !isUserSub
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const platformName = EPlatformsNames.SHOPIFY;
  const platform = Platform[platformName];
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [platformInfo, setPlatformInfo] = useState<PlatformProps>(
    {
      platformName: platformName,
      subPlan: SubscriptionMockData[ESubscriptionPlans.NO_SUB],
      items: repos?.map(i => ({...i, isActive: (trialState === 'active' || subInfo?.status === 'active')})) || [],
      filteredItems: repos?.map(i => ({...i, isActive: (trialState === 'active' || subInfo?.status === 'active')})) || [],
      users: users || [],
    },
  );

  const [paymentPoppup, setPaymentPoppup] = useState<IPaymentRequiredPoppup>({
    isOpen: false,
    platformName,
  });
  const [paymentState, setPaymentState] = useState<EPaymentRequiredType>(EPaymentRequiredType.NON);
  const [buyShowed, setBuyShowed] = useState<boolean>(false);
  const [showSettings2, setShowSettings2] = useState<boolean>(false);
  const [deleteDate, setDeleteData] = useState<{ id?: string, name?: string, type?: string }>({});
  const [deleteBulkDate, setDeleteBulkData] = useState<Array<string>>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectList, setSelectList] = useState<Array<string>>([]);
  const [progressItem, setProgressItem] = useState<string>('');
  const appWasDeleted = !!users && !hasUserApp && platformInfo?.items?.length !== 0
  const [progressEnd, setProgressEnd] = useState<boolean>(false);
  const [actionStatus, setActionStatus] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [intervalFunc, setIntervalFunc] = useState<any>();
  const [userId, setUserId] = useState<number>(1);
  const [userCanBackup, setUserCanBackup] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [addingItemsData, setAddingItemsData] = useState<{ addedItems: number; totalItems: number }>({ addedItems: 0, totalItems: 0});
  const {onMessage} = useWebSocket();

  useEffect(() => {
    const isExt = searchParams.get('isCloudStorageConnected');
    const errorMessage = searchParams.get('error_message');

    if (!errorMessage && isExt) {
      setShowSettings2(true)
    }
  })

  const clearState = (notPage?: boolean) => {
    setDeleteData({})
    setDeleteBulkData([])
    setSelectList([])
    setSelectAll(false)
    if (!notPage) {
      setPage(0)
    }
  }

  useEffect(() => {
    setUserCanBackup(canUserBackup)
  }, [canUserBackup])

  const restartBadge = () => {
    if (intervalFunc) {
      clearInterval(intervalFunc)
      setIntervalFunc(null)
    }
    setProgressEnd(false)
    setProgressItem('')
  }

  const fetchData = async (newPage?: number, isSocket?: boolean, search?: string) => {
    try {
      if (!isSocket) {
        setIsLoading(true)
        restartBadge()
      }
      if (!users) {
        await dispatch(fetchShopifyUser()).unwrap().catch(err => console.log(err));
      }

        await dispatch(fetchShopifyEntity({
          page: newPage || 0,
          perPage: 10,
          entity: entityType || 'products',
          search
        })).unwrap()
          .catch((err) => {
            console.log(err)
            setIsLoading(false)
            const error = err as IError;
            fetchItemsErrorHandler(error)
          })
          .finally(() => {
            setIsLoading(false)
          })

    } catch (e) {
      console.log(e);
      setIsLoading(false)
    }
  };

  useEffect(() => {
    if (platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) {
      navigate('/app-connector')
    }
    fetchData();

    onMessage((data) => {
      if (data.platform === EPlatformsNames.SHOPIFY && data.type === 'backup') {
        if (data.status === 'initialized') {
          setActionStatus('backing_up_process')
          setProgressEnd(false)
        } else if (data.status === 'success') {
          setProgressEnd(true)
          fetchData(page, true)
          setActionStatus('')
        }
      }
      if(EWebsocketMethods.itemsAddedCountChange === data.method && data.platform === EPlatformsNames.SHOPIFY){
        setAddingItemsData({addedItems:data.addedItems,totalItems:data.totalItems})
        if(data.addedItems === data.totalItems){
          fetchData()
        }
      }
    });
  }, []);

  useEffect(() => {
    if ((platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours) ||
      (platformInfo.subPlan.type === ESubscriptionPlans.SUBSCRIPTION && !platformInfo.subPlan.isPaid)) {
      setPaymentState(
        platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && !platformInfo.subPlan.periodHours ?
          EPaymentRequiredType.TRIAL : platformInfo.subPlan.isPaid ?
            EPaymentRequiredType.NON : EPaymentRequiredType.SUB,
      );
    } else {
      setPaymentState(EPaymentRequiredType.NON)
    }
  }, [platformInfo]);

  useEffect(() => {
    if (isUserSub && !roleApp?.[platformName]) {
      navigate('/', {replace: true})
    }
  }, [isUserSub, roleApp]);

  useEffect(() => {
    if (!isUserSub && !isLoadingRepos && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && !buyShowed) {
      setBuyShowed(true)
      Swal.fire({
        title: !!subInfo ? translate('notifications.subscription.subscribe_buy') : translate('notifications.subscription.subscribe_buy_trial'),
        text: !!subInfo ? translate('notifications.subscription.subscribe_buy_text') : translate('notifications.subscription.subscribe_buy_text_trial'),
        cancelButtonText: translate('notifications.choices.close'),
        confirmButtonText: translate('notifications.choices.buy_subscription_now'),
        showCancelButton: true,
        imageUrl: errorBigIcon,
      })
        .then((res) => {
          if (res.isConfirmed) {
            navigate('/platform/dashboard/shopify-prices-page')
          }
        })
    }
  }, [isLoadingRepos, subInfo]);

  const onConfirmDeleteRepo = async (id?, name?, isDelBulk?) => {
    try {
      // setIsLoading(true)
      const payload: IDeletePayload = {}
      let key = ''
      if (!!userId) {
        payload.placeId = userId
        key = 'reposIds'
      } else {
        key = 'gistsIds'
      }
      payload[key] = []
      if (!isDelBulk) {
        payload[key].push({id: id})
      } else {
        deleteBulkDate.forEach(item => {
          payload[key].push({id: item})
        })
      }
      let newPage = page
      if ((selectAll || platformInfo.items?.length === 1) && page > 0) {
        newPage = page - 1
        setPage(newPage)
      }
      await dispatch(deleteGithubItem(payload)).unwrap()
      await dispatch(fetchShopifyEntity({page: newPage, perPage: 10, entity: entityType})).unwrap()

      dispatch(getAllSubscriptions()).unwrap()

      restartBadge()
      clearState(true)

      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: isDelBulk ?
          translate('notifications.github_repos.successfully_deleted_many') :
          !!userId ?
            translate('notifications.github_repos.successfully_deleted') :
            translate('notifications.github_repos.successfully_deleted_gist'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
      // setIsLoading(false)

    } catch (err) {
      const error = err as IError;
      // setIsLoading(false)

      await Swal.fire({
        title: translate('notifications.titles.error'),
        text: decoratePopUpMessage(error.error as string),
        imageUrl: errorBigIcon,
        confirmButtonText: translate('notifications.choices.close'),
      });
    }
  };

  const handleDeleteRepo = (id, name, type) => {
    setDeleteData({id, name, type});
  };

  const handleDeleteBulkBoard = async (arr) => {
    setDeleteBulkData(arr);
  };


  const closeTrialPoppup = useCallback(() => {
    setPaymentPoppup({...paymentPoppup, isOpen: false});
  }, []);

  const validationBlock = useCallback(() => {
    if ((!!users && isShopConfirmed && platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
      (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED && !repos?.length)) {
      return <NoSubscriptionBlock platformName={platformName} isOwner={!isUserSub}/>;
    }
    if (!users?.length) {
      return <NoBackupsBlock
        isAdmin={!isUserSub}
        action={() => navigate('/app-connector')}
        platformItem={translate('views.bulk_restore.itemName.shopify.one')}
        actionText={translate('forms.github_add_edit_repo.configure_account')}
      />;
    }

    if ((!hasUserApp) && platformInfo.items?.length === 0) {
      return <UserNotConfirmedBlock noApp={true} isOwner={!isUserSub}/>;
    }

    if (!!users?.length && (!isShopConfirmed && !platformInfo.items?.length) || (!isShopConfirmed && hasUserApp && !!platformInfo.items?.length)) {
      return <UserNotConfirmedBlock isOwner={!isUserSub}/>;
    }

    return null;
  }, [platformInfo, trialState, users, paymentState]);

  const backupAllNow = async () => {
    try {
      setIsLoading(true)
      await dispatch(backupShopifyItem()).unwrap();
      // dispatch(fetchItemsForGithubUser(userId)).unwrap()

      restartBadge()
      if (total === 1) {
        setProgressItem(translate(`notifications.all.actionBadge.one.backing_up_process`, {
          app: 'Shopify',
          item: platformInfo.items?.[0]?.repo?.split('::')[0]
        }))
      } else {
        setProgressItem(translate(`notifications.all.actionBadge.many.backup`, {app: 'Shopify'}))
      }

      if (!isTestUser) {
        setUserCanBackup(false)
      }

      await Swal.fire({
        title: translate('notifications.titles.success'),
        text: total > 1 ? translate('notifications.github_repos.multi_start_backup') : translate('notifications.github_repos.single_start_backup'),
        toast: true,
        position: 'top-end',
        timerProgressBar: true,
        showConfirmButton: false,
        showCloseButton: true,
        imageUrl: toastSuccess,
        timer: 3000,
      });
      setIsLoading(false)
    } catch (err) {
      const error = err as IError;
      setIsLoading(false)

      if (error.code === 'err.user.backup.limit') {
        setUserCanBackup(false)
        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton: false,
          showCloseButton: true,
          imageUrl: iconNotifyWarningInfo,
          timer: 3000,
        });
      } else {
        await Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
          confirmButtonText: translate('notifications.choices.close'),
        });
      }
    }
  }

  useEffect(() => {
    if (!userLoading) {
      const subPlan = getSubInfo(trialState, subInfo, trialDays, platformName, trialQuantity, trialPlanQuantity);
      setPlatformInfo((prev) => ({
        ...prev,
        subPlan: {...subPlan},
      }));
    }
  }, [trialState, userLoading, trialDays, subInfo, trialQuantity, trialPlanQuantity]);

  useEffect(() => {
    if (!isLoadingRepos && !isLoadingGithubUsers) {
      setPlatformInfo((prev) => ({
        ...prev,
        items: repos?.map(i => ({...i, isActive: (trialState === 'active' || subInfo?.status === 'active')})) || [],
        users: users || [],
        filteredItems: repos?.map(i => ({...i, isActive: (trialState === 'active' || subInfo?.status === 'active')})) || [],
      }));

      const filt = repos?.filter(itemB => !!itemB.actionProcess?.length || itemB.status === 'initialized')
      if (filt && filt.length > 1 && !progressItem.length) {
        const isRest = filt.filter(itemB => itemB.actionProcess === 'restoring_process')
        filt.length === isRest?.length ?
          setProgressItem(translate(`notifications.all.actionBadge.many.restore`, {app: 'Shopify'})) :
          !!isRest?.length ?
            setProgressItem(translate(`notifications.all.actionBadge.multi`)) :
            setProgressItem(translate(`notifications.all.actionBadge.many.backup`, {app: 'Shopify'}))
      } else if (filt?.length === 1 && !progressItem.length) {
        setProgressItem(translate(`notifications.all.actionBadge.one.${filt[0].actionProcess || 'backing_up_process'}`, {
          app: 'Shopify',
          item: filt[0].repo?.split('::')[0]
        }))
      } else if (!filt?.length && !!progressItem?.length && !actionStatus) {
        if (intervalFunc) {
          clearInterval(intervalFunc)
        }
        setProgressEnd(true)
      }
    }
  }, [repos, users, isLoadingRepos, isLoadingGithubUsers]);

  const debouncedSearch = useCallback(
    debounce((event) => {
      setPage(0)
      fetchData(0, false, event.toLowerCase())
    }, 1000),
    [],
  );

  const onSearch = useCallback((event) => {
    const val = event.target.value || ''
    setSearchTerm(val);
    debouncedSearch(val)
  }, []);

  return (
    <div id="platform-content" className='list-view'>
      {iniFrame() &&
        <BadgesIFrameMode/>
      }
      {!!progressItem?.length && (
        <BadgesBackup progressItem={progressItem} platformName={platformName} isComplete={progressEnd}
                      onClose={() => restartBadge()}/>
      )}
      {addingItemsData.totalItems > 0 && addingItemsData.addedItems > 0 && (
        <BadgesAddingItems
          platformName={platformName} addingItemsData={addingItemsData}
          onClose={() => setAddingItemsData({addedItems: 0, totalItems: 0})}
        />
      )}
      {trialState === 'active' && trialDays && (
        <BadgesTrial trialDays={trialDays} platformName={platformName}/>
      )}
      {!isLoadingRepos && trialState === 'expired' && (!subInfo || subInfo?.status !== 'active') && (
        <BadgesTrial platformName={platformName}
                     color={!!repos?.filter(i => i.lastBackup !== '-').length ? 'error' : 'error_2'} isSub={!!subInfo}/>
      )}
      {appWasDeleted && (
        <BadgesAppWasDeleted platformName={platformName}/>
      )}
      {subInfo && subInfo?.isCancel && subInfo?.finishDateInMillisecond && ((subInfo?.finishDateInMillisecond - Date.now()) / (24 * 60 * 60 * 1000)) < 1 && (
        <BadgesSubscription platformName={platformName}/>
      )}
      <div className="header-block actions-header">
        <div className='actions-header-left' onClick={() => {
          navigate(`/platform/dashboard/shopify`)
        }}>
          <ArrowLeft className="arrow-icon"/>
          Back to Shop entities
        </div>
      </div>
      <div className="header-block">
        <Typography variant="h1" gutterBottom display="inline">
          {translate('forms.github_add_edit_repo.your_items', {
            platform: (platform?.title + ':'),
            itemName: entityType
          })}
        </Typography>

        <div className="search-block">
          <div className="action-button">
            {(trialState === 'active' || subInfo || !!platformInfo?.users?.length) && !isLoadingGithubUsers && hasUserApp && (
              <CustomTooltip
                title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}
              >
                <Button variant="outlined" color="primary"
                        disabled={platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial' || paymentState !== EPaymentRequiredType.NON}
                        onClick={() => setShowSettings2(true)}>
                  External storage
                </Button>
              </CustomTooltip>
            )}
            {!!repos?.length && ((userCanBackup || isTestUser) ? (
              <CustomTooltip
                title={(platformInfo.subPlan?.name !== 'Pro' && platformInfo.subPlan?.name !== 'Trial') ? 'This feature is only available with a Pro Plan subscription' : ''}>
                <Button
                  ml={2} variant="outlined"
                  color="primary"
                  onClick={() => (platformInfo.subPlan?.name === 'Pro' || platformInfo.subPlan?.name === 'Trial') ? backupAllNow() : updateSubscriptionPlanModal({
                    translate,
                    platformName,
                    navigate
                  })}
                  disabled={(platformInfo.subPlan.type === ESubscriptionPlans.NO_SUB && trialState === ETrialStatus.NOT_STARTED) ||
                    (platformInfo.subPlan.type === ESubscriptionPlans.TRIAL && trialState === ETrialStatus.EXPIRED) || appWasDeleted ||
                    paymentState !== EPaymentRequiredType.NON || !isAdminRole}
                >
                  <DatabaseIcoInvert className="feather-icon-in-button"/>
                  {translate('views.platform_pages.backupNow')}
                </Button>
              </CustomTooltip>
            ) : (
              <CustomTimerTooltip setUserCanBackup={() => setUserCanBackup(true)}>
                <Button
                  ml={2} variant="outlined"
                  color="primary" onClick={backupAllNow}
                  disabled={true}
                >
                  <DatabaseIcoInvert className="feather-icon-in-button"/>
                  {translate('views.platform_pages.backupNow')}
                </Button>
              </CustomTimerTooltip>
            ))}
          </div>
        </div>
      </div>
      <div className="header-block header-block-sub">
        <div className="user-name">
          {platformInfo && !!platformInfo.users?.length && (
            <Typography variant="h4" gutterBottom display="inline">
              @{platformInfo.users[0].username}
            </Typography>
          )}
        </div>
        <div className="search-block">
          {(!!repos?.length || !!searchTerm) && (
            <SearchInput
              className="search-field"
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(event) => {
                onSearch(event)
              }}
              endAdornment={
                <InputAdornment position="end" className="search-icon">
                  <img src={searchIcon} loading="lazy"/>
                </InputAdornment>
              }
            />
          )}
        </div>
      </div>

      <Box sx={{width: '100%'}}>
        {isLoadingGithubUsers || isLoadingRepos || userLoading || isLoading ?
          <div className="spinner-wrapper">
            <CircularProgress/>
          </div> :
          <div className="data-block">
            {validationBlock()}
            {!validationBlock() &&
              <>
                <div className="repo-wrapper">
                  <div className="repos-list">
                    <PlatformItemsList
                      items={platformInfo.filteredItems?.map(i => ({...i, actionProcess: actionStatus})) || []}
                      updItems={() => dispatch(fetchShopifyEntity({
                        page: page,
                        perPage: 10,
                        entity: entityType
                      })).unwrap()}
                      platformName={platformName}
                      subPlatform={entityType}
                      onDelete={handleDeleteRepo}
                      onBulkDelete={handleDeleteBulkBoard}
                      // onBackup={(id, name, type) => {
                      //   return (
                      //     type ?
                      //       dispatch(backupGithubItem(name)).unwrap() :
                      //       dispatch(backupGithubGist(id)).unwrap()
                      //   )
                      // }}
                      setSelectAll={(isSelect) => {
                        if (!isSelect) {
                          setSelectList([])
                          setSelectAll(isSelect)
                        } else {
                          const listToSelect = platformInfo.filteredItems?.filter(item => !item.actionProcess && item.status !== "initialized").map(item => item.id) || []
                          setSelectList(listToSelect)
                          if (listToSelect.length === platformInfo.filteredItems?.length) {
                            setSelectAll(isSelect)
                          }
                        }
                      }}
                      selectAll={selectAll}
                      setSelectList={(name) => (
                        setSelectList(prevState => {
                          const indexToRemove = prevState.indexOf(name);
                          if (indexToRemove !== -1) {
                            prevState.splice(indexToRemove, 1);
                          } else {
                            prevState.push(name)
                          }
                          if (!!prevState?.length && !!platformInfo.filteredItems?.length &&
                            ((prevState.length === platformInfo.filteredItems.length && !selectAll) ||
                              (prevState.length !== platformInfo.filteredItems.length && selectAll))) {
                            setSelectAll(prevState.length === total)
                          }
                          if (!prevState.length) {
                            setSelectAll(false)
                          }
                          return [...prevState]
                        })
                      )}
                      selectList={selectList}
                      subPlan={platformInfo.subPlan}
                      trialState={trialState}
                      appWasDeleted={appWasDeleted}
                      total={total || 0}
                      page={page}
                      setPage={(p) => {
                        setPage(p)
                        clearState(true)
                        fetchData(p, false, searchTerm)
                      }}
                      isLoading={isLoading}
                      isAdmin={isAdminRole}
                    />
                  </div>
                </div>
              </>
            }
            <PaymentRequiredPoppup
              isOpen={paymentPoppup.isOpen}
              closeNotification={closeTrialPoppup}
              type={paymentState}
              platformName={paymentPoppup.platformName}
            />
            <Modal
              isOpen={showSettings2}
              className='ext-storage-modal'
              closeNotification={() => setShowSettings2(false)}
              child={<ExternalStorage
                platformModal='shopify'
                closeModal={() => setShowSettings2(false)}
              />}
            />
            <DeleteItemDialog
              isOpen={!!deleteDate?.name?.length || !!deleteBulkDate.length}
              closeNotification={() => {
                clearState(true)
              }}
              platformName={platformName}
              isBulk={!!deleteBulkDate.length}
              bulkData={deleteBulkDate}
              singleData={deleteDate}
              paymentState={paymentState}
              items={platformInfo.filteredItems || []}
              name={deleteDate?.name || ''}
              updItems={() => dispatch(fetchShopifyEntity({
                page: page,
                perPage: 10,
                entity: entityType
              })).unwrap()}
              onConfirm={() => {
                deleteBulkDate.length > 1 ?
                  onConfirmDeleteRepo(deleteDate?.id, deleteDate?.name, true) :
                  onConfirmDeleteRepo(deleteBulkDate[0])
              }}
            />
          </div>
        }
      </Box>
    </div>
  );
};

export default withRedirectAfterLogout()(ShopifyEntityList);
